import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, Plus } from "lucide-react";
import { FadeIn, ChapterLabel } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Newsletter from "@/components/Newsletter";
import { CHARACTERS, NEWS, BOOKS, WORLD_HOTSPOTS, YOUTUBE_URL, BOOKS_BY_URL } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const featured = BOOKS.find((e) => e.featured);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <section ref={ref} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src="/images/hero-dunes.png" alt="Golden sand dunes at sunset" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-cream via-cream/30 to-cream/10" />

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-8 top-28 hidden animate-floaty md:block lg:right-24"
      >
        <img src="/images/char-baseball.png" alt="The Magic Baseball" className="h-24 w-24 rounded-full border-4 border-cream object-cover shadow-xl lg:h-32 lg:w-32" />
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-40 md:px-12 md:pb-28">
        <FadeIn delay={0.6} className="flex flex-col gap-3 sm:flex-row">
          <Link
            to="/stories"
            data-testid="hero-begin-journey-button"
            className="group flex h-14 items-center justify-center gap-2 rounded-full bg-sky px-8 text-sm font-semibold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-[1.03]"
          >
            Begin the Journey
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-watch-trailer-button"
            className="flex h-14 items-center justify-center gap-3 rounded-full border border-charcoal/20 bg-cream/70 px-8 text-sm font-semibold uppercase tracking-widest text-charcoal backdrop-blur-sm transition-colors duration-300 hover:border-forest hover:text-forest"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-forest text-cream"><Play size={11} fill="currentColor" /></span>
            Watch Trailer
          </a>
        </FadeIn>
      </div>
    </section>
  );
};

const FeaturedStory = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="featured-story-section">
    <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
      <FadeIn className="relative mx-auto w-full max-w-md lg:mx-0">
        <div className="absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/25 blur-3xl" />
        <motion.img
          src={featured.cover}
          alt={`${featured.title} key art`}
          whileHover={{ rotate: 0, scale: 1.02 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-full rotate-[-2.5deg] rounded-3xl object-cover shadow-[0_40px_80px_-30px_rgba(34,34,34,0.4)]"
        />
      </FadeIn>
      <div>
        <ChapterLabel num="01" title="The First Story" />
        <FadeIn delay={0.1}>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky">{featured.chapter}</p>
          <h2 className="mt-3 font-serif text-4xl leading-tight tracking-tight md:text-5xl">{featured.title}</h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-charcoal/70 md:text-lg">{featured.description}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {featured.themes.map((t) => (
              <span key={t} className="rounded-full bg-forest/10 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-forest">{t}</span>
            ))}
          </div>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to={`/stories/${featured.id}`} data-testid="featured-learn-more-button" className="group flex h-13 items-center justify-center gap-2 rounded-full bg-charcoal px-8 py-4 text-sm font-semibold uppercase tracking-widest text-cream transition-transform duration-300 hover:scale-[1.03]">
              Learn More <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" data-testid="featured-watch-button" className="flex items-center justify-center gap-2 rounded-full border border-charcoal/20 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-charcoal transition-colors duration-300 hover:border-forest hover:text-forest">
              <Play size={14} /> Watch the Story
            </a>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

const CharactersPreview = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="characters-section">
    <ChapterLabel num="02" title="The Characters" />
    <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <FadeIn delay={0.1}>
        <h2 className="max-w-md font-serif text-4xl leading-tight tracking-tight md:text-5xl">Meet the hearts of the stories</h2>
      </FadeIn>
      <FadeIn delay={0.2}>
        <Link to="/characters" data-testid="characters-view-all-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
          All Characters <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeIn>
    </div>
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {CHARACTERS.map((c, i) => (
        <FadeIn key={c.id} delay={i * 0.1}>
          <Link to="/characters" data-testid={`character-card-${c.id}`} className="group block overflow-hidden rounded-3xl bg-white shadow-[0_20px_40px_-20px_rgba(47,107,60,0.15)] transition-shadow duration-500 hover:shadow-[0_30px_60px_-20px_rgba(47,107,60,0.3)]">
            <div className="overflow-hidden">
              <img src={c.img} alt={c.name} className="aspect-square w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            </div>
            <div className="p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">{c.role}</p>
              <h3 className="mt-1 font-serif text-2xl">{c.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{c.short}</p>
            </div>
          </Link>
        </FadeIn>
      ))}
    </div>
  </section>
);

const WorldSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  return (
    <section ref={ref} className="py-24 md:py-32" data-testid="world-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <ChapterLabel num="03" title="The World" />
        <FadeIn delay={0.1} className="mb-12 max-w-2xl">
          <h2 className="font-serif text-4xl leading-tight tracking-tight md:text-5xl">Look out over the world he carries</h2>
          <p className="mt-4 text-base leading-relaxed text-charcoal/70 md:text-lg">
            Golden deserts, rolling oceans, snow-capped peaks, green valleys, whispering forests, and sunlit beaches. Wander the places from The Places We Carry, and tap the markers to look closer.
          </p>
        </FadeIn>
      </div>
      <FadeIn className="relative h-[65vh] overflow-hidden md:h-[75vh]">
        <motion.img src="/images/world-pano.png" alt="The boy gazing over a world of deserts, oceans, snow peaks, valleys, forests and beaches" style={{ x }} className="h-full w-full scale-[1.15] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/30 via-transparent to-cream/20" />
        {WORLD_HOTSPOTS.map((h) => (
          <div key={h.name} className="group absolute" style={{ left: h.x, top: h.y }}>
            <button
              data-testid={`hotspot-${h.name.toLowerCase().replace(/\s+/g, "-")}`}
              aria-label={h.name}
              className="relative flex h-9 w-9 items-center justify-center rounded-full border border-cream/60 bg-cream/25 text-cream backdrop-blur-sm transition-transform duration-300 hover:scale-110"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold/40" />
              <Plus size={15} />
            </button>
            <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-3 w-56 -translate-x-1/2 rounded-2xl border border-charcoal/10 bg-cream/95 p-4 opacity-0 shadow-xl backdrop-blur transition-all duration-300 group-hover:opacity-100">
              <p className="font-serif text-lg text-charcoal">{h.name}</p>
              <p className="mt-1 text-xs leading-relaxed text-charcoal/65">{h.desc}</p>
            </div>
          </div>
        ))}
      </FadeIn>
    </section>
  );
};

const MediaPreview = () => {
  const cards = [
    { title: "Series Trailer", note: "Watch on YouTube", img: "/images/hero.png", to: YOUTUBE_URL, external: true, testid: "media-card-trailer" },
    { title: "Artwork Gallery", note: "Step inside the frames", img: "/images/gallery-night.png", to: "/media", testid: "media-card-gallery" },
    { title: "Behind the Scenes", note: "From sketch to screen", img: "/images/gallery-sketch.png", to: "/media", testid: "media-card-bts" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="media-section">
      <ChapterLabel num="04" title="The Studio" />
      <FadeIn delay={0.1} className="mb-14">
        <h2 className="max-w-lg font-serif text-4xl leading-tight tracking-tight md:text-5xl">Watch, wander, and peek behind the curtain</h2>
      </FadeIn>
      <div className="grid gap-6 md:grid-cols-3">
        {cards.map((c, i) => {
          const inner = (
            <>
              <img src={c.img} alt={c.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/10 to-transparent" />
              {i === 0 && (
                <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-cream/90 text-charcoal shadow-xl transition-transform duration-300 group-hover:scale-110">
                  <Play size={20} fill="currentColor" />
                </span>
              )}
              <div className="absolute bottom-0 left-0 p-6">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">{c.note}</p>
                <h3 className="mt-1 font-serif text-2xl text-cream">{c.title}</h3>
              </div>
            </>
          );
          const cls = "group relative block h-80 overflow-hidden rounded-3xl";
          return (
            <FadeIn key={c.title} delay={i * 0.1}>
              {c.external ? (
                <a href={c.to} target="_blank" rel="noopener noreferrer" data-testid={c.testid} className={cls}>{inner}</a>
              ) : (
                <Link to={c.to} data-testid={c.testid} className={cls}>{inner}</Link>
              )}
            </FadeIn>
          );
        })}
      </div>
    </section>
  );
};

const NewsPreview = () => (
  <section className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32" data-testid="news-section">
    <ChapterLabel num="05" title="The Journal" />
    <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
      <FadeIn delay={0.1}>
        <h2 className="max-w-md font-serif text-4xl leading-tight tracking-tight md:text-5xl">Latest from the Vale</h2>
      </FadeIn>
      <FadeIn delay={0.2}>
        <Link to="/news" data-testid="news-view-all-link" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
          All News <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeIn>
    </div>
    <div className="grid gap-6 md:grid-cols-3">
      {NEWS.map((n, i) => (
        <FadeIn key={n.id} delay={i * 0.1}>
          <Link to="/news" data-testid={`news-card-${n.id}`} className="group block">
            <div className="overflow-hidden rounded-3xl">
              <img src={n.img} alt={n.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
            </div>
            <div className="mt-5 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em]">
              <span className="text-gold">{n.tag}</span>
              <span className="h-1 w-1 rounded-full bg-charcoal/30" />
              <span className="text-charcoal/50">{n.date}</span>
            </div>
            <h3 className="mt-2 font-serif text-2xl leading-snug transition-colors duration-300 group-hover:text-forest">{n.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-charcoal/60">{n.excerpt}</p>
          </Link>
        </FadeIn>
      ))}
    </div>
  </section>
);

export default function Home() {
  useSEO(
    "The Cap Chronicles: Picture Books & Read-Aloud Stories for Kids Ages 4-8",
    "Step into The Cap Chronicles: illustrated picture books and YouTube read-aloud stories for kids ages 4-8 about courage, kindness, self-belief, and the magic in ordinary moments."
  );
  return (
    <main>
      <Hero />

      <section className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-24" data-testid="new-book-section">
        <FadeIn>
          <div className="grid items-center gap-12 rounded-[2.5rem] border border-charcoal/10 bg-white p-8 shadow-[0_30px_70px_-30px_rgba(47,107,60,0.25)] md:p-14 lg:grid-cols-2 lg:gap-16">
            <div className="relative mx-auto w-full max-w-sm">
              <div className="absolute left-1/2 top-1/2 h-[105%] w-[105%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/25 blur-3xl" />
              <img src="/images/alglw-cover.png" alt="A Little Goes a Long Way activity and coloring book cover" className="relative w-full rotate-[2deg] rounded-3xl object-cover shadow-[0_40px_80px_-30px_rgba(34,34,34,0.4)]" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky">New Release · Activity & Coloring Book</p>
              <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight md:text-5xl" data-testid="new-book-title">A Little Goes a Long Way</h2>
              <p className="mt-3 font-serif text-3xl italic text-forest" data-testid="new-book-price">$11.99</p>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-charcoal/70 md:text-lg">
                A Little Goes a Long Way was inspired by the real-world work behind The DITR, where sports equipment and opportunities have been shared with children in Africa. The book turns that idea into an interactive journey kids can explore through coloring, activities, travel, and discovery.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                <a href={BOOKS_BY_URL} target="_blank" rel="noopener noreferrer" data-testid="order-book-button" className="group inline-flex h-14 items-center justify-center gap-2 rounded-full bg-gold px-10 text-sm font-bold uppercase tracking-widest text-charcoal shadow-lg transition-transform duration-300 hover:scale-[1.04]">
                  Order the Book <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <Link to="/stories/a-little-goes-a-long-way" data-testid="new-book-learn-more" className="group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-forest">
                  Learn More About the Story <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      <Marquee />
      <FeaturedStory />
      <CharactersPreview />
      <WorldSection />
      <MediaPreview />
      <NewsPreview />
      <Newsletter />
    </main>
  );
}
